import type {SiteContainerTone} from "./site-containers";

export type SiteContainerFillBarProps = {
  /** How full, 0–100. */
  percent?: number;
  /** The oil share of `percent`, 0–100, drawn on top of the water. */
  oilPercent?: number;
  /** The haulable top of the fill, 0–100; the fill below it is washed out. */
  readyPercent?: number;
  tone?: SiteContainerTone;
  label: string;
  className?: string;
};

const clamp = (value: number) => Math.max(0, Math.min(100, value));

/**
 * A container's level: water with oil floating on top, the part that can be hauled now drawn solid and the rest
 * (below the minimum level) washed out. Critical containers turn red.
 */
export const SiteContainerFillBar = ({percent, oilPercent = 0, readyPercent, tone = "normal", label, className}: SiteContainerFillBarProps) => {
  const fill = clamp(percent ?? 0);
  const oil = Math.min(fill, clamp(oilPercent));
  const water = fill - oil;
  const ready = readyPercent == null ? fill : Math.min(fill, clamp(readyPercent));
  const critical = tone === "critical";
  return <div
    role="meter"
    aria-label={label}
    aria-valuemin={0}
    aria-valuemax={100}
    aria-valuenow={percent == null ? undefined : Math.round(fill)}
    className={[
      "relative h-2.5 w-full overflow-hidden rounded-full bg-[var(--ots-surface-variant)]",
      tone === "warning" ? "ring-1 ring-[var(--ots-warning)]" : "",
      className,
    ].filter(Boolean).join(" ")}>
    {critical
      ? <span data-fill="critical" className="absolute inset-y-0 left-0 bg-[var(--ots-error)]" style={{width: `${fill}%`}}/>
      : <>
        <span data-fill="water" className="absolute inset-y-0 left-0 bg-[var(--ots-transport-category-water)]" style={{width: `${water}%`}}/>
        <span data-fill="oil" className="absolute inset-y-0 bg-[var(--ots-transport-category-oil)]" style={{left: `${water}%`, width: `${oil}%`}}/>
      </>}
    {/* Washes out what sits below the haulable part. */}
    {ready < fill && (
      <span data-fill="unready" className="absolute inset-y-0 left-0 bg-[var(--ots-surface)] opacity-55" style={{width: `${fill - ready}%`}}/>
    )}
  </div>;
};
